/**
 * SearchUsersUseCase
 *
 * Searches users by first name and last name (dual search),
 * deduplicates results, and manages pagination cursors.
 * Matches Flutter's search_screen.dart searchUsers logic.
 *
 * Web improvement over Flutter: drops users that touch a blocked
 * relationship with the current user, via
 * `shouldFilterOutBlockedUserUseCase`. The userService already filters out
 * users whose own `blockedUsers` list contains the current uid (i.e. users
 * who blocked me, using fresh server data); this filter additionally drops
 * users *I've* blocked, using the in-memory `currentUser.blockedUsers`
 * array. No per-row server fetch — both lists are on the loaded UserEntity.
 */

import type { PageCursor } from '@/data/types/json';
import { UserService } from '@/domain/services/user/userService';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { shouldFilterOutBlockedUserUseCase } from '@/domain/usecases/user/shouldFilterOutBlockedUserUseCase';

export interface SearchUsersParams {
  keyword: string;
  limitCount?: number;
  lastFetchedByFirstName?: PageCursor;
  lastFetchedByLastName?: PageCursor;
  hasMoreByFirstName?: boolean;
  hasMoreByLastName?: boolean;
  currentUser?: UserEntity | null;
}

export interface SearchUsersResult {
  users: UserEntity[];
  hasMoreByFirstName: boolean;
  hasMoreByLastName: boolean;
  lastFetchedByFirstName?: PageCursor;
  lastFetchedByLastName?: PageCursor;
}

export class SearchUsersUseCase {
  private userService: UserService;

  constructor() {
    this.userService = UserService.getInstance();
  }

  /**
   * Execute dual search (firstName + lastName), deduplicate results
   */
  async execute(params: SearchUsersParams): Promise<SearchUsersResult> {
    const {
      keyword,
      limitCount = 6,
      lastFetchedByFirstName,
      lastFetchedByLastName,
      hasMoreByFirstName = true,
      hasMoreByLastName = true,
      currentUser = null,
    } = params;

    const allUsers: UserEntity[] = [];
    let newLastFetchedByFirstName = lastFetchedByFirstName;
    let newLastFetchedByLastName = lastFetchedByLastName;
    let newHasMoreByFirstName = hasMoreByFirstName;
    let newHasMoreByLastName = hasMoreByLastName;

    // Search by first name
    if (hasMoreByFirstName) {
      const resultByFirstName = await this.userService.searchUsers(
        keyword,
        lastFetchedByFirstName,
        limitCount,
        false
      );
      allUsers.push(...resultByFirstName.users);
      newLastFetchedByFirstName = resultByFirstName.cursor;
      newHasMoreByFirstName = resultByFirstName.hasMore;
    }

    // Search by last name
    if (hasMoreByLastName) {
      const resultByLastName = await this.userService.searchUsers(
        keyword,
        lastFetchedByLastName,
        limitCount,
        true
      );

      // Deduplicate: only add users not already in the list
      for (const user of resultByLastName.users) {
        if (!allUsers.some(u => u.firebaseId === user.firebaseId)) {
          allUsers.push(user);
        }
      }
      newLastFetchedByLastName = resultByLastName.cursor;
      newHasMoreByLastName = resultByLastName.hasMore;
    }

    const visibleUsers = allUsers.filter(
      user => !shouldFilterOutBlockedUserUseCase.execute(user.firebaseId, currentUser),
    );

    return {
      users: visibleUsers,
      hasMoreByFirstName: newHasMoreByFirstName,
      hasMoreByLastName: newHasMoreByLastName,
      lastFetchedByFirstName: newLastFetchedByFirstName,
      lastFetchedByLastName: newLastFetchedByLastName,
    };
  }
}

export const createSearchUsersUseCase = () => new SearchUsersUseCase();
